'use client';

import { useMemo, useSyncExternalStore } from 'react';
import { Document } from '@contentful/rich-text-types';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';

type PznContent = Document | string;

function applyTokens(content: PznContent, tokens: Record<string, string>): PznContent {
    if (typeof content === 'string') {
        return content.replace(/\{(\w+)\}/g, (match, key) => tokens[key] ?? match);
    }
    return JSON.parse(
        JSON.stringify(content).replace(/\{(\w+)\}/g, (match, key) => tokens[key] ?? match),
    );
}

interface PznMessageProps {
    template: PznContent;
    fallback?: PznContent;
    fnFallback?: PznContent;
}

export function PznMessage({ template, fallback, fnFallback }: PznMessageProps): React.JSX.Element {
    const rawCookie = useSyncExternalStore(
        () => () => {},
        () => document.cookie.match(/sxm_user_info=([^;]+)/)?.[1] ?? '',
        () => null,
    );

    const { resolved, visible } = useMemo(() => {
        // null = server snapshot: stay hidden to match SSR and avoid flash
        if (rawCookie === null) return { resolved: template, visible: false };

        const templateStr = typeof template === 'string' ? template : JSON.stringify(template);
        const needsFn = templateStr.includes('{fn}');
        const needsJday = templateStr.includes('{jday}');

        if (rawCookie) {
            try {
                const userInfo = JSON.parse(decodeURIComponent(rawCookie));
                const fnSatisfied = !needsFn || !!userInfo.fn;
                const jdaySatisfied = !needsJday || (userInfo.jday !== undefined && userInfo.jday > 0);

                if (fnSatisfied && jdaySatisfied) {
                    const tokens: Record<string, string> = {};
                    if (needsFn) tokens.fn = userInfo.fn;
                    if (needsJday) tokens.jday = String(userInfo.jday);
                    return { resolved: applyTokens(template, tokens), visible: true };
                }

                if (needsFn && needsJday && !!userInfo.fn && !jdaySatisfied && fnFallback) {
                    return { resolved: applyTokens(fnFallback, { fn: userInfo.fn }), visible: true };
                }
            } catch {}
        }

        if (fallback !== undefined) return { resolved: fallback, visible: true };
        return { resolved: template, visible: false };
    }, [template, fallback, fnFallback, rawCookie]);

    const content = typeof resolved === 'string'
        ? <span>{resolved}</span>
        : <RenderRTE contentfulDoc={resolved} />;

    return (
        <div style={{ visibility: visible ? 'visible' : 'hidden' }}>
            {content}
        </div>
    );
}
